import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AdminDaytonaOverview,
  AdminGcpOverview,
  AdminLambdaOverview,
  AdminOverview,
  Viewer,
} from "./types";

// ─── Toasts ──────────────────────────────────────────────────────────────────

export type ToastTone = "info" | "success" | "warning" | "danger";

export type AdminToast = {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
};

export type Toasts = ReturnType<typeof useToasts>;

/**
 * Toast state for the DS `ToastStack`. Danger toasts never auto-dismiss: a
 * failed start/stop/delete is something an operator has to actually read.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<AdminToast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, title: string, message?: string) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, tone, title, message }]);
    const duration = tone === "danger" ? 0 : tone === "warning" ? 8000 : 5000;
    if (duration > 0) {
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, duration),
      );
    }
    return id;
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  return useMemo(
    () => ({
      toasts,
      dismiss,
      info: (title: string, message?: string) => push("info", title, message),
      success: (title: string, message?: string) => push("success", title, message),
      warning: (title: string, message?: string) => push("warning", title, message),
      error: (title: string, message?: string) => push("danger", title, message),
    }),
    [toasts, dismiss, push],
  );
}

// ─── Confirm dialog ──────────────────────────────────────────────────────────

export type ConfirmRequest = {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
};

/**
 * Promise-returning confirm backed by the DS `Modal`, replacing the blocking
 * `window.confirm` both old pages used. The caller awaits a boolean; the page
 * shell renders whatever `pending` holds.
 */
export function useConfirm() {
  const [pending, setPending] = useState<
    (ConfirmRequest & { resolve: (ok: boolean) => void }) | null
  >(null);

  const requestConfirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...request, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (ok: boolean) => {
      setPending((current) => {
        current?.resolve(ok);
        return null;
      });
    },
    [],
  );

  return { pending, requestConfirm, confirm: () => settle(true), cancel: () => settle(false) };
}

export type Confirm = ReturnType<typeof useConfirm>;

// ─── Auto refresh ────────────────────────────────────────────────────────────

// ─── Viewport ────────────────────────────────────────────────────────────────

/** `--fwfa-bp-md`, the DS tablet breakpoint. */
const MD_QUERY = "(min-width: 48rem)";

/**
 * True from the DS `md` breakpoint up. A media query rather than CSS because the
 * thing it switches is a DS prop, not a declaration: `StatRow`'s `spread` and
 * `compact` are mutually exclusive registers, so picking between them by width
 * has to happen where the prop is passed. Seeded from `matchMedia` in the state
 * initialiser, so the first paint is already correct.
 */
export function useAtLeastMd() {
  const [matches, setMatches] = useState(() => window.matchMedia(MD_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(MD_QUERY);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return matches;
}

const REFRESH_SECONDS = 30;

/**
 * Opt-in 30s poller. `skip` lets the page hold off while an action is in
 * flight, so a background refresh cannot stomp an optimistic row.
 */
export function useAutoRefresh(callback: () => Promise<void>, skip: () => boolean) {
  const [enabled, setEnabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(REFRESH_SECONDS);
  const callbackRef = useRef(callback);
  const skipRef = useRef(skip);
  callbackRef.current = callback;
  skipRef.current = skip;

  useEffect(() => {
    if (!enabled) {
      setSecondsLeft(REFRESH_SECONDS);
      return;
    }
    setSecondsLeft(REFRESH_SECONDS);
    const countdown = setInterval(() => {
      setSecondsLeft((seconds) => (seconds <= 1 ? REFRESH_SECONDS : seconds - 1));
    }, 1000);
    const poll = setInterval(() => {
      if (!skipRef.current()) void callbackRef.current();
      setSecondsLeft(REFRESH_SECONDS);
    }, REFRESH_SECONDS * 1000);
    return () => {
      clearInterval(countdown);
      clearInterval(poll);
    };
  }, [enabled]);

  return { enabled, secondsLeft, toggle: useCallback(() => setEnabled((value) => !value), []) };
}

// ─── Data ────────────────────────────────────────────────────────────────────

export type AdminData = {
  viewer: Viewer | null;
  overview: AdminOverview | null;
  daytona: AdminDaytonaOverview | null;
  lambda: AdminLambdaOverview | null;
  gcp: AdminGcpOverview | null;
  /** Per-provider soft failures. A 503 means "not configured" and is not a notice. */
  notices: Record<string, string>;
  loading: boolean;
  /** Fatal: no viewer, not an admin, or the core overview failed. */
  error: string | null;
  reload: () => Promise<void>;
  reloadSilent: () => Promise<void>;
  setOverview: React.Dispatch<React.SetStateAction<AdminOverview | null>>;
  setDaytona: React.Dispatch<React.SetStateAction<AdminDaytonaOverview | null>>;
  setLambda: React.Dispatch<React.SetStateAction<AdminLambdaOverview | null>>;
  setGcp: React.Dispatch<React.SetStateAction<AdminGcpOverview | null>>;
};

function hasAdminRole(roles?: string): boolean {
  if (!roles) return false;
  return roles.split(",").map((role) => role.trim().toLowerCase()).includes("admin");
}

async function getJson<T>(url: string): Promise<
  { ok: true; data: T } | { ok: false; status: number; detail: string }
> {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    return { ok: false, status: response.status, detail: await response.text() };
  }
  return { ok: true, data: (await response.json()) as T };
}

/**
 * Loads the viewer and every provider overview in one pass. Providers that are
 * not configured answer 503; those are absent, not broken, so they render an
 * empty state rather than an error.
 */
export function useAdminData(): AdminData {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [daytona, setDaytona] = useState<AdminDaytonaOverview | null>(null);
  const [lambda, setLambda] = useState<AdminLambdaOverview | null>(null);
  const [gcp, setGcp] = useState<AdminGcpOverview | null>(null);
  const [notices, setNotices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent: boolean) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const who = await fetch("/api/user", { credentials: "include" });
      if (who.status === 401) {
        window.location.href = "/api/auth/login";
        return;
      }
      const user = await who.json();
      if (user.error) {
        if (!silent) setError("Could not determine the current user.");
        return;
      }
      setViewer({ id: user.id, email: user.email, roles: user.roles });
      if (!hasAdminRole(user.roles)) {
        if (!silent) setError("Admin access denied. You do not have permission to view this page.");
        return;
      }

      const nextNotices: Record<string, string> = {};

      const core = await getJson<AdminOverview>("/api/admin/overview");
      if (!core.ok) {
        if (!silent) setError(core.detail || "Failed to load the admin overview.");
        return;
      }
      setOverview(core.data);

      const [daytonaResult, lambdaResult, gcpResult] = await Promise.all([
        getJson<AdminDaytonaOverview>("/api/admin/daytona/overview"),
        getJson<AdminLambdaOverview>("/api/admin/lambda-microvms/overview"),
        getJson<AdminGcpOverview>("/api/admin/gcp/overview"),
      ]);

      if (daytonaResult.ok) setDaytona(daytonaResult.data);
      else {
        setDaytona(null);
        if (daytonaResult.status !== 503) {
          nextNotices.daytona = daytonaResult.detail || "Daytona admin data is unavailable.";
        }
      }

      if (lambdaResult.ok) setLambda(lambdaResult.data);
      else {
        setLambda(null);
        if (lambdaResult.status !== 503) {
          nextNotices.lambda = lambdaResult.detail || "AWS Lambda admin data is unavailable.";
        }
      }

      if (gcpResult.ok) setGcp(gcpResult.data);
      else {
        setGcp(null);
        if (gcpResult.status !== 503) {
          nextNotices.gcp = gcpResult.detail || "GCP admin data is unavailable.";
        }
      }

      setNotices(nextNotices);
    } catch {
      if (!silent) setError("Unable to load admin data. Check the web server is reachable, then refresh.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const reload = useCallback(() => load(false), [load]);
  const reloadSilent = useCallback(() => load(true), [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    viewer, overview, daytona, lambda, gcp, notices, loading, error,
    reload, reloadSilent, setOverview, setDaytona, setLambda, setGcp,
  };
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export type RunAction = {
  /** Pending key, conventionally `${rowId}:${action}`. Drives the row's busy state. */
  key: string;
  url: string;
  body?: unknown;
  confirm?: ConfirmRequest;
  successTitle: string;
  successMessage?: string;
  failureTitle: string;
  /** Applied before the request; pair with `revert` to undo on failure. */
  optimistic?: () => void;
  revert?: () => void;
  /**
   * Runs on success with the parsed body, if the response carried JSON. Return
   * a string to override `successMessage` when the detail only exists in the
   * response, as with reconcile's removed-mapping count.
   */
  onSuccess?: (payload: unknown) => string | void;
  /** Refetch after success. Default true; set false when `onSuccess` patches state itself. */
  reloadAfter?: boolean;
};

export type Actions = ReturnType<typeof useActions>;

/**
 * One POST-with-feedback path for every provider: confirm, mark pending, fire,
 * toast the outcome, revert on failure. Sections declare intent, not plumbing.
 */
export function useActions(toasts: Toasts, confirm: Confirm, reload: () => Promise<void>) {
  const [pending, setPending] = useState<Set<string>>(new Set());

  const isPending = useCallback((key: string) => pending.has(key), [pending]);
  const isBusy = pending.size > 0;

  const run = useCallback(
    async (action: RunAction) => {
      if (action.confirm && !(await confirm.requestConfirm(action.confirm))) return;

      setPending((prev) => new Set(prev).add(action.key));
      action.optimistic?.();

      try {
        const response = await fetch(action.url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(action.body ?? {}),
        });

        if (!response.ok) {
          action.revert?.();
          toasts.error(action.failureTitle, (await response.text()) || undefined);
          return;
        }

        const text = await response.text();
        let payload: unknown = null;
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch {
            payload = null;
          }
        }

        const override = action.onSuccess?.(payload);
        toasts.success(action.successTitle, override || action.successMessage);
        if (action.reloadAfter !== false) await reload();
      } catch {
        action.revert?.();
        toasts.error(action.failureTitle, "Network error.");
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(action.key);
          return next;
        });
      }
    },
    [confirm, toasts, reload],
  );

  return { pending, isPending, isBusy, run };
}
